import { FC, useState } from 'react';
import { FeatureViewFCO, FeatureViewFCOFields } from '../../../core/types/fcoTypes';
import { NewClusterConfig } from '../../../types/tecton_proto/args/feature_view';
import { ClusterConfigType } from '../../../utils/feature-view/feature-view-util';
import { convertValueToHumanReadableValue } from '../../featureViews/featureViewUtil';

import { ViewDefinitionFlyout } from '@tecton/ComponentRedesign';
import TectonFCOCard, { TectonFCOListItem } from '@tecton/ComponentRedesign/FCOCard';

import { prettifyJson } from '@tecton/ComponentRedesign/utils';
import { useUserSettings } from '../../context/UserSettingsContext';
import SparkConfigFlyout from './SparkConfigFlyout';

interface MaterializationBatchConfigurationProps {
  fco: FeatureViewFCO;
}

const ExistingClusterCard: FC<MaterializationBatchConfigurationProps> = () => {
  return <></>;
};

const NewDatabricksClusterCard: FC<MaterializationBatchConfigurationProps> = ({ fco }) => {
  const config = fco[FeatureViewFCOFields.BATCH_CLUSTER_CONFIG]?.clusterConfig as NewClusterConfig;

  const availability =
    config?.instance_availability && convertValueToHumanReadableValue(config?.instance_availability) ? (
      convertValueToHumanReadableValue(config?.instance_availability)
    ) : (
      <>n/a</>
    );

  const items: TectonFCOListItem[] = [
    {
      title: 'Instance Type',
      content: config.instance_type ?? <>n/a</>,
    },
    {
      title: 'Instance Availability',
      content: availability,
    },
    {
      title: 'Number of Workers',
      content: config?.number_of_workers ?? <>n/a</>,
    },
    {
      title: 'First On-Demand',
      content: config?.first_on_demand ?? <>n/a</>,
    },
    {
      title: 'Root Volume Size in (gb)',
      content: config?.root_volume_size_in_gb ?? <>n/a</>,
    },
    {
      title: 'Extra pip Dependencies',
      content: config?.extra_pip_dependencies ? (
        <ViewDefinitionFlyout
          type="Extra pip Dependencies"
          definition={config?.extra_pip_dependencies?.join('\n') ?? ''}
        />
      ) : (
        <>n/a</>
      ),
    },
    {
      title: 'Spark Config',
      content: config?.spark_config ? (
        <ViewDefinitionFlyout type="Spark Config" definition={prettifyJson(config?.spark_config)} />
      ) : (
        <>n/a</>
      ),
    },
    {
      title: 'Pinned Spark Version',
      content: config?.pinned_spark_version ?? <>n/a</>,
    },
  ];

  return <TectonFCOCard title="Batch Configuration" items={items} />;
};

const NewEmrClusterCard: FC<MaterializationBatchConfigurationProps> = ({ fco }) => {
  const config = fco[FeatureViewFCOFields.BATCH_CLUSTER_CONFIG]?.clusterConfig as NewClusterConfig;

  const availability =
    config?.instance_availability && convertValueToHumanReadableValue(config?.instance_availability) ? (
      convertValueToHumanReadableValue(config?.instance_availability)
    ) : (
      <>n/a</>
    );

  const items: TectonFCOListItem[] = [
    {
      title: 'Instance Type',
      content: config.instance_type ?? <>n/a</>,
    },
    {
      title: 'Instance Availability',
      content: availability,
    },
    {
      title: 'Number of Workers',
      content: config?.number_of_workers ?? <>n/a</>,
    },
    {
      title: 'First On-Demand',
      content: config?.first_on_demand ?? <>n/a</>,
    },
    {
      title: 'Root Volume Size in (gb)',
      content: config?.root_volume_size_in_gb ?? <>n/a</>,
    },
    {
      title: 'Extra pip Dependencies',
      content: config?.extra_pip_dependencies ? (
        <ViewDefinitionFlyout
          type={'Extra pip Dependencies'}
          definition={config?.extra_pip_dependencies?.join('\n') ?? ''}
        />
      ) : (
        <>n/a</>
      ),
    },
    {
      title: 'Spark Config',
      content: config?.spark_config ? (
        <ViewDefinitionFlyout type="Spark Config" definition={prettifyJson(config?.spark_config)} />
      ) : (
        <>n/a</>
      ),
    },
    {
      title: 'EMR Version',
      content: config?.pinned_spark_version ?? <>n/a</>,
    },
  ];

  return <TectonFCOCard title="Batch Configuration" items={items} />;
};

const ImplicitConfigClusterCard: FC<MaterializationBatchConfigurationProps> = () => {
  return <></>;
};

const JsonDatabricksClusterCard: FC<MaterializationBatchConfigurationProps> = ({ fco }) => {
  const { workspace } = useUserSettings();
  const items = [
    {
      title: 'Spark Config',
      content: (
        <>
          <SparkConfigFlyout workspace={workspace ?? 'prod'} fco={fco} configType="batch" />
        </>
      ),
    },
  ];
  return <TectonFCOCard title="Batch Configuration" items={items} />;
};

const JsonEmrClusterCard: FC<MaterializationBatchConfigurationProps> = ({ fco }) => {
  const { workspace } = useUserSettings();
  useState<boolean>(false);
  const items = [
    {
      title: 'Spark Config',
      content: (
        <>
          <SparkConfigFlyout workspace={workspace ?? 'prod'} fco={fco} configType="batch" />
        </>
      ),
    },
  ];

  return <TectonFCOCard title="Batch Configuration" items={items} />;
};

const JsonDataProcClusterCard: FC<MaterializationBatchConfigurationProps> = () => {
  return <></>;
};

const TectonBatchComputeClusterCard: FC<MaterializationBatchConfigurationProps> = () => {
  return <></>;
};

const FeatureViewMaterializationBatchConfigurationCard: FC<MaterializationBatchConfigurationProps> = ({ fco }) => {
  // Based on this notion doc: https://www.notion.so/tecton/Feature-View-Materialization-Card-f2d4de1e58414decb5d6a2e237371f75
  const configuration = fco[FeatureViewFCOFields.BATCH_CLUSTER_CONFIG];

  switch (configuration?.type) {
    case ClusterConfigType.EXISTING_CLUSTER:
      return <ExistingClusterCard fco={fco} />;
    case ClusterConfigType.NEW_DATABRICKS:
      return <NewDatabricksClusterCard fco={fco} />;
    case ClusterConfigType.NEW_EMR:
      return <NewEmrClusterCard fco={fco} />;
    case ClusterConfigType.IMPLICIT_CONFIG:
      return <ImplicitConfigClusterCard fco={fco} />;
    case ClusterConfigType.JSON_DATABRICKS:
      return <JsonDatabricksClusterCard fco={fco} />;
    case ClusterConfigType.JSON_EMR:
      return <JsonEmrClusterCard fco={fco} />;
    case ClusterConfigType.JSON_DATAPROC:
      return <JsonDataProcClusterCard fco={fco} />;
    case ClusterConfigType.RIFT:
      return <TectonBatchComputeClusterCard fco={fco} />;
  }

  return <></>;
};

export default FeatureViewMaterializationBatchConfigurationCard;
